import React from 'react'
import { Clock, Zap, Shield, BarChart3, Activity, ArrowUpRight, Timer } from 'lucide-react'
import { useLanguage } from '../i18n'
import MiningDashboard from '../components/MiningDashboard'

const MiningPage = ({ isConnected, userAddress }) => {
  const { t } = useLanguage()
  
  const miningStats = [
    { icon: Activity, value: '24,892', label: t('mining.totalMining'), change: 15.2, changeLabel: 'Today', color: 'gold' },
    { icon: BarChart3, value: '12.5%', label: t('mining.successRate'), change: 2.1, changeLabel: 'YoY', color: 'green' },
    { icon: Timer, value: '12h', label: t('mining.miningCycle'), change: 0, changeLabel: t('mining.fixed'), color: 'blue' },
    { icon: Shield, value: '99.9%', label: t('mining.availability'), change: 0, changeLabel: t('mining.online'), color: 'cyan' }
  ]

  const features = [
    {
      icon: Clock,
      title: t('mining.timeWindow'),
      description: t('mining.timeWindowDesc')
    },
    {
      icon: Shield,
      title: t('mining.smartContract'),
      description: t('mining.smartContractDesc')
    },
    {
      icon: Zap,
      title: t('mining.antiCheat'),
      description: t('mining.antiCheatDesc')
    },
    {
      icon: Activity,
      title: t('mining.earlyReward'),
      description: t('mining.earlyRewardDesc')
    }
  ]

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-titanium-gold/10 border border-titanium-gold/30 rounded-full px-4 py-2 mb-6">
            <Clock className="w-4 h-4 text-titanium-gold" />
            <span className="text-sm font-semibold text-titanium-gold uppercase tracking-wider">{t('mining.miningSystem')}</span>
          </div>
          <h1 className="tech-title text-5xl md:text-6xl font-bold mb-4">
            {t('mining.title')}
          </h1>
          <p className="tech-subtitle text-xl max-w-3xl mx-auto">
            {t('mining.subtitle')}
          </p>
        </div>

        {/* Mining Stats */}
        <div className="mb-12">
          <div className="tech-grid tech-grid-4">
            {miningStats.map((stat, idx) => (
              <div key={idx} className="stat-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.change !== undefined && (
                      <div className={`stat-change ${stat.change >= 0 ? 'positive' : 'negative'}`}>
                        {stat.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4 rotate-180" />}
                        <span>{Math.abs(stat.change)}%</span>
                        {stat.changeLabel && <span className="ml-1 opacity-70">{stat.changeLabel}</span>}
                      </div>
                    )}
                  </div>
                  <div className="icon-tech">
                    <stat.icon className="w-7 h-7" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Features */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="tech-title text-3xl font-bold mb-3">{t('mining.coreFeatures')}</h2>
            <p className="tech-subtitle text-lg">{t('mining.coreFeaturesDesc')}</p>
          </div>
          <div className="tech-grid tech-grid-2">
            {features.map((feature, idx) => (
              <div key={idx} className="glass-card p-8">
                <div className="icon-tech mb-6">
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3 className="tech-title text-xl font-bold mb-3">{feature.title}</h3>
                <p className="tech-subtitle">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mining Dashboard */}
        <div className="glass-card gold-border">
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="tech-title text-3xl font-bold mb-3">{t('mining.dashboard')}</h2>
              <p className="tech-subtitle text-lg">{t('mining.dashboardDesc')}</p>
            </div>
            <MiningDashboard
              isConnected={isConnected}
              walletAddress={userAddress}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default MiningPage
